import React from 'react';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import nextheralogo from '../assets/nextheralogo.png';

const PrivacyPolicy = () => {
  const handleGoBack = () => {
    window.location.href = '/';
  };

  return (
    <PolicyContainer>
      <PolicyHeader>
        <BackButton onClick={handleGoBack}>
          <ArrowLeft size={20} />
          Back to Home
        </BackButton>
        <Logo src={nextheralogo} alt="NexThera" />
      </PolicyHeader>

      <PolicyContent>
        <Title>Privacy Policy</Title>
        <LastUpdated>Last updated: 24 August 2025</LastUpdated>
        
        <Section>
          <Paragraph>
            NexThera respects your privacy. This notice explains, in plain language, what we do with personal data when you browse our website, contact us, or interact with our marketing. It does not replace the product-specific notices and Data Processing Agreements we sign with hospitals and research partners for our B2B platform - those govern any clinical or special-category data in our services.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Who we are</SectionTitle>
          <Paragraph>
            NEXTHERAPEUTICS, LDA (VAT: PT518543404) is the controller for website and marketing data. Our registered head office is Rua Junta de Freguesia, 194, CI3 Nextherapeutics LDA, Vila Pavão, Escariz, 4540-322 Arouca, Portugal. You can reach us at <EmailLink href="mailto:mail@nexthera.tech">mail@nexthera.tech</EmailLink>.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>What we collect and why</SectionTitle>
          <Paragraph>
            When you write to us, request a demo or subscribe to updates, we receive the details you share - typically your name, work email, phone, organisation, role and the message itself. If you apply for a role, we process the usual application materials. As you use our site, we generate limited technical records (such as IP address, timestamps, pages visited, device and browser information) to keep the site secure and reliable, and - if you agree via our cookie banner - to understand usage and improve the experience.
          </Paragraph>
          <Paragraph>
            We only process personal data for clear purposes: operating and protecting the website; responding to enquiries; sending you updates you asked for; improving the site through consent-based analytics; handling recruitment; and meeting legal or contractual requirements. Our legal bases are legitimate interests for operating a secure, useful site; consent for non-essential cookies and direct marketing; performance of a contract or pre-contract steps for demos or recruitment; and legal obligation where the law requires record-keeping.
          </Paragraph>
          <Paragraph>
            We do not want health or other special-category data through our public forms. If you include such information accidentally, we minimise or delete it. For the avoidance of doubt: special-category data in our B2B platform is processed under a separate contract and lawful bases specific to healthcare and research.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Cookies</SectionTitle>
          <Paragraph>
            We use only what we need. Essential cookies keep the site running and safe. With your consent we add privacy-respecting analytics and a few functional preferences. Please check our Cookie Policy documentation for more details.
          </Paragraph>
          
          <CookieCategory>
            <CategoryTitle>Essential (always on)</CategoryTitle>
            <CategoryDescription>
              Security, load balancing, consent storage
            </CategoryDescription>
          </CookieCategory>

          <CookieCategory>
            <CategoryTitle>Optional with consent</CategoryTitle>
            <CategoryDescription>
              Analytics and limited functional preferences
            </CategoryDescription>
          </CookieCategory>
        </Section>

        <Section>
          <SectionTitle>Sharing and transfers</SectionTitle>
          <Paragraph>
            We don't sell personal data. We use carefully chosen service providers - for secure hosting, email delivery, CRM, analytics, error monitoring and recruitment - who act under contract and only on our instructions. If required by law, we may disclose data to authorities. Should we ever restructure the company (for example, a merger or acquisition), personal data may transfer under appropriate safeguards and continued protection.
          </Paragraph>
          <Paragraph>
            We prefer to store and process data in the EEA. If we need to transfer personal data internationally, we rely on recognised safeguards such as EU adequacy decisions and the European Commission's Standard Contractual Clauses, supplemented as necessary.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>How long we keep data</SectionTitle>
          <Paragraph>
            We keep data only as long as needed for the purposes above and then delete or anonymise it. As a guide: enquiry and contact data are retained up to 24 months after our last interaction; consent logs last as long as necessary for audit; security logs are generally shorter (up to 12 months); recruitment files for unsuccessful candidates are removed after 12 months unless you ask us to keep them longer. Contract and account records with B2B customers follow statutory limitations and accounting rules.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Your rights</SectionTitle>
          <Paragraph>
            You can ask us to access your data, correct it, delete it, limit or object to certain uses, or provide you a copy to reuse elsewhere. Where we rely on consent (for example, analytics or newsletters), you can withdraw it at any time. To exercise your rights, email <EmailLink href="mailto:mail@nexthera.tech">mail@nexthera.tech</EmailLink> with "Privacy Request" in the subject; we may need to verify your identity. You also have the right to contact your local data protection authority. In Portugal this is the CNPD. We'll respond within one month and tell you if we need a little longer for complex requests.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Security</SectionTitle>
          <Paragraph>
            We protect personal data with administrative, technical and organisational measures appropriate to the risks: role-based access, encryption in transit, secure development practices, monitoring and alerting, vendor due-diligence and regular reviews. No system is perfect, but if a personal-data breach occurs we will act promptly and notify you and/or regulators where the law requires it.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Children</SectionTitle>
          <Paragraph>
            Our site targets professional audiences. We don't knowingly collect data from children under 16. If you believe a child has provided data to us, please contact us and we'll take appropriate action.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Changes</SectionTitle>
          <Paragraph>
            If we change how we handle personal data, we'll update this page and adjust the "Last updated" date. Where the changes are material, we will provide a more prominent notice.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Contact</SectionTitle>
          <Paragraph>
            Questions or requests about privacy are welcome at <EmailLink href="mailto:mail@nexthera.tech">mail@nexthera.tech</EmailLink>. Postal enquiries can be sent to our registered head office in Arouca, Portugal.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>Regional notes</SectionTitle>
          
          <RegionalNote>
            <RegionalTitle>United Kingdom</RegionalTitle>
            <Paragraph>
              For UK visitors and customers, references to the GDPR mean the UK GDPR and the Data Protection Act 2018. International transfers from the UK use the UK Addendum to the EU Standard Contractual Clauses or the UK IDTA, as applicable. You may raise concerns with the UK Information Commissioner's Office (ICO).
            </Paragraph>
          </RegionalNote>

          <RegionalNote>
            <RegionalTitle>California (USA)</RegionalTitle>
            <Paragraph>
              We do not sell or share personal information for cross-context behavioural advertising. California residents may request to know, correct or delete their personal information and may opt out of "sale" or "sharing" (where applicable). You also have the right not to be discriminated against for exercising these rights. To submit a request, email <EmailLink href="mailto:mail@nexthera.tech">mail@nexthera.tech</EmailLink> with "California Privacy Request" in the subject.
            </Paragraph>
          </RegionalNote>
        </Section>
      </PolicyContent>
    </PolicyContainer>
  );
};

const PolicyContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8fafc, #ffffff);
  padding: 0;
`;

const PolicyHeader = styled.header`
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #6b7280;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #2094F1;
  }
`;

const Logo = styled.img`
  height: 50px;
  width: auto;
  margin: 0;
`;

const PolicyContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const LastUpdated = styled.p`
  font-size: 0.9rem;
  color: #6b7280;
  text-align: center;
  margin-bottom: 3rem;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: #374151;
  margin-bottom: 1rem;
`;

const EmailLink = styled.a`
  color: #2094F1;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CookieCategory = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const CategoryTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: #2094F1;
  margin-bottom: 0.5rem;
`;

const CategoryDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #4b5563;
  margin: 0;
`;

const RegionalNote = styled.div`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const RegionalTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #92400e;
  margin-bottom: 0.5rem;
`;

export default PrivacyPolicy;
